import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../i18n/LanguageContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const navLinks = [
    { name: t('nav.home'), path: '/', hash: null },
    { name: t('nav.tours'), path: '/tours', hash: null },
    { name: t('nav.about'), path: '/', hash: 'about' },
    { name: t('nav.reviews'), path: '/', hash: 'reviews' },
    { name: t('nav.blog'), path: '/blog', hash: null },
    { name: t('nav.contact'), path: '/', hash: 'contact' },
  ];

  const isActive = (path) => {
    if (path !== '/') return location.pathname === path;
    return location.pathname === '/';
  };

  const handleNavClick = (link) => {
    setIsOpen(false);
    
    if (link.hash) {
      // If we're not on the home page, navigate there first
      if (location.pathname !== '/') {
        navigate('/');
        // Wait for navigation then scroll
        setTimeout(() => {
          const element = document.getElementById(link.hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      } else {
        // We're on home page, wait for menu to close then scroll
        setTimeout(() => {
          const element = document.getElementById(link.hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } else {
      // Navigate to page and scroll to top
      navigate(link.path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <nav className="fixed w-full z-50 bg-white border-b border-gray-100">
      {/* Top Bar */}
      <div className="hidden md:block bg-[#1D4E41] text-white py-2">
        <div className="container mx-auto px-6 max-w-7xl flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:+50688173167" className="flex items-center gap-2 hover:text-[#F08A5D] transition-colors">
              <Phone className="w-4 h-4" />
              +506 8817-3167
            </a>
            <a href="mailto:reservations@travelguide.cr" className="flex items-center gap-2 hover:text-[#F08A5D] transition-colors">
              <Mail className="w-4 h-4" />
              reservations@travelguide.cr
            </a>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Costa Rica
          </div>
        </div>
      </div>

      {/* Main Nav */}
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex justify-between items-center h-20 md:h-36">
          <Link to="/" className="flex items-center h-full py-1" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_a1eb42dc-1756-4f7a-b3c1-591c05407e32/artifacts/udsnjmd2_1000353198.png" 
              alt="Travel Guide Costa Rica" 
              className="h-full w-auto"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link)}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-lg font-medium transition-colors hover:text-[#1D4E41] ${
                  isActive(link.path) ? 'text-[#1D4E41]' : 'text-gray-600'
                }`}
              >
                {link.name}
              </button>
            ))}
            <LanguageSwitcher />
            <Link
              to="/tours"
              data-testid="nav-book-now"
              className="h-12 px-8 bg-[#F08A5D] hover:bg-[#E0794C] text-white rounded-full font-medium text-lg flex items-center transition-all active:scale-95"
            >
              {t('nav.bookNow')}
            </Link>
          </div>

          {/* Mobile Menu Button + Language Switcher */}
          <div className="md:hidden flex items-center gap-1">
            <LanguageSwitcher />
            <button
              className="p-2"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 animate-fade-in">
          <div className="container mx-auto px-4 py-4 space-y-4">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link)}
                className="block w-full text-left py-2 text-gray-600 hover:text-[#1D4E41] font-medium"
              >
                {link.name}
              </button>
            ))}
            <Link
              to="/tours"
              onClick={() => setIsOpen(false)}
              className="block w-full py-3 bg-[#F08A5D] text-white text-center rounded-full font-medium"
            >
              {t('nav.bookNow')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
